import { useEffect, useState } from 'react'
import { useStops, useEta, useStopByCode } from '@/lib/queries'
import { MapView } from './MapView'
import { Directions } from './Directions'
import { StopHeader } from './StopHeader'
import type { StopInfo, BusEta } from '@/lib/api'
import { DataError } from './DataError'

interface EtaViewProps {
  routeCode: string
  stopCode: string
  lineId: string
  routeDestination: string
  isFavorite: boolean
  onToggleFavorite: () => void
  onStopLoaded?: (stop: StopInfo) => void
  translations: {
    loading: string
    min: string
    stopsAway: string
    bus: string
    nowAt: string
    noBuses: string
    passed: string
    busesPassed: string
    otherBuses: string
    stops: string
    refreshEta: string
    official: string
    late: string
    early: string
  }
  lang: 'el' | 'en'
}

export function EtaView({
  routeCode,
  stopCode,
  lineId,
  routeDestination,
  isFavorite,
  onToggleFavorite,
  onStopLoaded,
  translations: t,
  lang
}: EtaViewProps) {
  const { data: stops, isLoading: stopsLoading, isError: stopsError, error: stopsQueryError, refetch: refetchStops } = useStops(routeCode)
  const stop = useStopByCode(stops, stopCode)

  const { data: etaResult, isLoading: etaLoading, refetch } = useEta(routeCode, stop || undefined)
  const [focusedBus, setFocusedBus] = useState<BusEta | null>(null)

  useEffect(() => {
    if (stop && onStopLoaded) {
      onStopLoaded(stop)
    }
  }, [stop, onStopLoaded])

  const getStopDescription = (s: StopInfo) =>
    lang === 'en' ? (s.StopDescrEng || s.StopDescr) : s.StopDescr

  const getDelayInfo = (bus: BusEta) => {
    if (bus.officialEtaMinutes === undefined) return null
    const diff = bus.etaMinutes - bus.officialEtaMinutes
    if (Math.abs(diff) < 1) return null
    return {
      diff,
      isLate: diff > 0,
      text: diff > 0 ? `+${diff} ${t.late}` : `${diff} ${t.early}`
    }
  }

  if (stopsLoading) {
    return (
      <div className="eta-view">
        <div className="loading">{t.loading}</div>
      </div>
    )
  }


  if (stopsError) {
    return <DataError error={stopsQueryError} lang={lang} onRetry={() => void refetchStops()} />
  }

  if (!stop) {
    return (
      <div className="eta-view">
        <div className="no-buses">Stop not found (code: {stopCode})</div>
      </div>
    )
  }

  if (etaLoading) {
    return (
      <div className="eta-view">
        <div className="loading">{t.loading}</div>
      </div>
    )
  }

  if (!etaResult) return null

  if (!etaResult.success) {
    return <DataError error={new Error(etaResult.error || 'ETA data is not available.')} lang={lang} onRetry={() => void refetch()} />
  }

  return (
    <div className="eta-view">
      <div className="eta-result">
        <StopHeader
          lineId={lineId}
          stopName={getStopDescription(stop)}
          destination={routeDestination}
          isFavorite={isFavorite}
          onToggleFavorite={onToggleFavorite}
          lang={lang}
        />

        {etaResult.nextBus ? (
          <button
            className={`next-bus ${focusedBus?.vehicleNumber === etaResult.nextBus.vehicleNumber ? 'focused' : ''}`}
            onClick={() => setFocusedBus(focusedBus?.vehicleNumber === etaResult.nextBus!.vehicleNumber ? null : etaResult.nextBus!)}
          >
            <div className="eta-time-row">
              {etaResult.nextBus.officialEtaMinutes !== undefined ? (
                <>
                  <div className="eta-time">{etaResult.nextBus.officialEtaMinutes} {t.min}</div>
                  {(() => {
                    const delay = getDelayInfo(etaResult.nextBus!)
                    return delay ? (
                      <span className={`delay-badge ${delay.isLate ? 'late' : 'early'}`}>
                        {delay.text}
                      </span>
                    ) : null
                  })()}
                </>
              ) : (
                <div className="eta-time">{etaResult.nextBus.etaMinutes} {t.min}</div>
              )}
            </div>
            <div className="eta-details">
              <span>{etaResult.nextBus.stopsAway} {t.stopsAway}</span>
              <span>{t.bus} #{etaResult.nextBus.vehicleNumber}</span>
              <span>{t.nowAt} {etaResult.nextBus.currentStop}</span>
            </div>
          </button>
        ) : etaResult.passedBuses && etaResult.passedBuses.length > 0 ? (
          <div className="next-bus passed">
            <div className="eta-time-row">
              <div className="eta-time passed-label">{t.passed}</div>
            </div>
            <div className="eta-details">
              <span>
                {etaResult.passedBuses.length}{' '}
                {lang === 'en'
                  ? (etaResult.passedBuses.length === 1 ? 'bus' : 'buses')
                  : (etaResult.passedBuses.length === 1 ? 'λεωφορείο' : 'λεωφορεία')}{' '}
                {t.busesPassed}
              </span>
            </div>
          </div>
        ) : (
          <div className="no-buses">{t.noBuses}</div>
        )}

        {etaResult.approachingBuses && etaResult.approachingBuses.length > 1 && (
          <div className="other-buses">
            <h3>{t.otherBuses}</h3>
            {etaResult.approachingBuses.slice(1).map(bus => {
              const delay = getDelayInfo(bus)
              return (
                <button
                  key={bus.vehicleNumber}
                  className={`bus-item ${focusedBus?.vehicleNumber === bus.vehicleNumber ? 'focused' : ''}`}
                  onClick={() => setFocusedBus(focusedBus?.vehicleNumber === bus.vehicleNumber ? null : bus)}
                >
                  <span>
                    {bus.officialEtaMinutes !== undefined ? bus.officialEtaMinutes : bus.etaMinutes} {t.min}
                    {delay && <span className={`delay-badge small ${delay.isLate ? 'late' : 'early'}`}>{delay.text}</span>}
                  </span>
                  <span>{bus.stopsAway} {t.stops}</span>
                  <span>#{bus.vehicleNumber}</span>
                </button>
              )
            })}
          </div>
        )}

        {stops && stops.length > 0 && (
          <MapView
            stops={stops}
            selectedStop={stop}
            buses={etaResult.approachingBuses}
            focusedBus={focusedBus}
            lineId={lineId}
            lang={lang}
            showRoute={true}
          />
        )}

        <Directions
          stopLat={parseFloat(stop.StopLat)}
          stopLng={parseFloat(stop.StopLng)}
          stopName={getStopDescription(stop)}
          lang={lang}
        />

        <button className="refresh-btn" onClick={() => refetch()}>
          🔄 {t.refreshEta}
        </button>
      </div>
    </div>
  )
}
